import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Observable } from 'rxjs/internal/Observable';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AbstractControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { combineLatest } from 'rxjs';
import {
    BaseNestedFieldDefinition,
    FieldType
} from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import {
    ActionItem,
    ActionItemStyle,
    ButtonActionItem
} from '../../../../../elements/components/action-item/entities/action-item.entity';
import { IconSize } from '../../../../../elements/components/icon/entities/icon-sizes.entity';
import { AbstractControlStatus } from '../../../../../form-controls/components/form-typed-value-list/base-complex/form-typed-value-list-complex-base.component';
import { FormValue, FormValueState } from '../../../../../form-controls/entities/form-value.entity';
import { throwIfNullOrUndefined } from '../../../../../helpers/typed-reactive-filters.helper';
import { FieldReadHelper } from '../../../../../profile-form/helpers/fields/field-read.helper';
import {
    ComplexListFieldValue,
    ComplexMapFieldValue,
    FieldValue
} from '../../../../entities/field-values/field-type-values.entity';
import { ComplexFormValueHelper } from '../../../../helpers/complex-form-value/complex-form-value.helper';
import { BatchFieldReadHelper } from '../../../../helpers/fields/batch-field-read-helper';
import { FieldValuesToRecords } from '../../../../helpers/records/flatten-complex-list-record';
import { ChildFieldToProfileField } from '../../../../helpers/to-profile-field/to-profile-field.helper';
import { ShBaseGenericInputFieldComponent } from '../../base-generic-input-field/base-generic-input-field.component';

@Component({
    selector: 'sh-complex-list-field',
    templateUrl: './complex-list-input-field.component.html',
    styleUrls: ['./complex-list-input-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShComplexListInputFieldComponent extends ShBaseGenericInputFieldComponent {
    public childDefinitionLabel$!: Observable<string>;
    public childDefinitionKey$!: Observable<string>;
    public childProfileField$!: Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>>;
    public grandChildProfileFields$!: Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>[]>;

    public canEdit$: Observable<boolean>;
    public globalActionItems$!: Observable<ActionItem[]>;
    public childActionItems$!: Observable<ActionItem[][]>;

    public list: UntypedFormArray;
    public flattenedComplexListFieldRecords: PartialRecord[];
    public isIndeterminate: boolean = false;

    public FieldType: typeof FieldType = FieldType;

    protected initialValueList: FieldValue[] = [];

    private listValueChanges$!: Observable<string[]>;
    private listStatusChanges$!: Observable<AbstractControlStatus>;

    constructor(
        protected fieldHelper: FieldReadHelper,
        protected formBuilder: UntypedFormBuilder
    ) {
        super();
        this.initChildDefinitionStream();
        this.initGenericFieldStreams();
        this.initInputControls();
    }

    init(profileField: ExtendedBaseProfileField, records: PartialRecord[], control: UntypedFormControl): void {
        const values: FormValue<ComplexListFieldValue>[] = this.isBatchEdit
            ? (BatchFieldReadHelper.getBatchFieldValue(
                  profileField.FieldDefinition
              ) as FormValue<ComplexListFieldValue>[])
            : this.fieldHelper.getMergedComplexListFieldValue(profileField.FieldDefinition, records);

        this.isIndeterminate = values.some(formValue => formValue.state === FormValueState.INDETERMINATE);

        this.initialValueList = values
            .filter(formValue => !!formValue.value && formValue.state === FormValueState.SET)
            .map(formValue => formValue.value);

        this.flattenedComplexListFieldRecords = this.initialValueList
            .map(recordListValue =>
                FieldValuesToRecords(recordListValue, (profileField.FieldDefinition.Children ?? [])[0].DottedKey)
            )
            .flat();

        if (!this.flattenedComplexListFieldRecords || this.flattenedComplexListFieldRecords.length === 0) {
            this.addEmptyValue();
        } else {
            this.flattenedComplexListFieldRecords.forEach(record => this.list.push(this.createFormGroup()));
        }

        this.initListChangeListener(profileField, control);
    }

    protected initListChangeListener(profileField: ExtendedBaseProfileField, control: UntypedFormControl): void {
        this.list.valueChanges.pipe(debounceTime(250), takeUntil(this.destroy$)).subscribe((formGroupValues: any[]) => {
            // Always UNSET all initial values
            const oldValues: FormValue<ComplexMapFieldValue>[] = this.initialValueList.map(
                value => new FormValue(value as ComplexMapFieldValue, FormValueState.UNSET)
            );

            // Generate a ComplexMapFieldValue for each formGroupValue
            // In this case only the SET values are relevant since we already UNSET the initial values
            const newValues: FormValue<ComplexMapFieldValue>[] = formGroupValues.map(
                formGroupValue =>
                    ComplexFormValueHelper.toMetadataValues(
                        formGroupValue,
                        (profileField.FieldDefinition.Children ?? [])[0]
                    )[0]
            );

            // This should return a list of the set and unset complex values
            const values: FormValue<ComplexMapFieldValue>[] = [...oldValues, ...newValues];
            control.setValue(values);
        });
    }

    protected initChildDefinitionStream(): void {
        this.childProfileField$ = this.profileField$.pipe(
            map(profileField => profileField.FieldDefinition.Children),
            map(children => {
                if (!children || children.length !== 1) {
                    throw new Error(`ComplexListField should have exactly 1 child, but has ${children?.length}`);
                }
                return children[0];
            }),
            withLatestFrom(this.profileField$),
            map(([childDef, profileField]) =>
                ChildFieldToProfileField(childDef, profileField, this.isBatchEdit, this.isIndeterminate)
            )
        );

        this.childDefinitionLabel$ = this.childProfileField$.pipe(
            map(childDefinition => childDefinition.FieldDefinition.Label)
        );
        this.childDefinitionKey$ = this.childProfileField$.pipe(
            map(childDefinition => childDefinition.FieldDefinition.Key)
        );

        const grandChildDefinitions$: Observable<BaseNestedFieldDefinition[]> = this.childProfileField$.pipe(
            map(childDefinition => childDefinition.FieldDefinition.Children),
            throwIfNullOrUndefined(`Expected ComplexField to have children`),
            map(children => {
                if (!Array.isArray(children)) {
                    throw new Error(`Expected ComplexField children to be an array but was ${children}`);
                }
                if (children.length === 0) {
                    throw new Error(`Expected ComplexField to have at least 1 child, but has ${children.length}`);
                }
                return children;
            })
        );

        this.grandChildProfileFields$ = combineLatest([grandChildDefinitions$, this.profileField$]).pipe(
            map(([children, profileField]) =>
                children.map(child =>
                    ChildFieldToProfileField(child, profileField, this.isBatchEdit, this.isIndeterminate)
                )
            )
        );
    }

    private initGenericFieldStreams(): void {
        this.canEdit$ = this.control$.pipe(map(control => control.enabled));
    }

    private initInputControls(): void {
        this.list = this.formBuilder.array([]);

        this.listValueChanges$ = this.list.valueChanges.pipe(distinctUntilChanged(isEqual), startWith(this.list.value));
        this.listStatusChanges$ = this.list.statusChanges.pipe(
            distinctUntilChanged(isEqual),
            startWith(this.list.status)
        );

        this.globalActionItems$ = this.canEdit$.pipe(
            distinctUntilChanged(isEqual),
            map(canEdit => this.getGlobalActionItems(canEdit))
        );

        this.childActionItems$ = combineLatest([this.listValueChanges$, this.listStatusChanges$, this.canEdit$]).pipe(
            distinctUntilChanged(isEqual),
            map(([valueChanges, statusChanges, canEdit]) => this.calculateChildActionItems(canEdit))
        );
    }

    // #region Action Items
    private calculateChildActionItems(canEdit: boolean): ActionItem[][] {
        return this.list.controls.map((_, index) => this.getChildActionItems(index, canEdit));
    }

    private getChildActionItems(index: number, canEdit: boolean): ActionItem[] {
        const removeAction: ButtonActionItem = {
            eventListener: this.removeItem.bind(this, index),
            type: 'button',
            style: ActionItemStyle.PureIconButton,
            icon: 'minus',
            label: '',
            iconSize: IconSize.REGULAR,
            disabled: !canEdit
        };

        return [removeAction];
    }

    private getGlobalActionItems(canEdit: boolean): ActionItem[] {
        const addAction: ButtonActionItem = {
            eventListener: this.addEmptyValue.bind(this),
            type: 'button',
            style: ActionItemStyle.PureIconButton,
            icon: 'add',
            label: '',
            iconSize: IconSize.REGULAR,
            disabled: !canEdit
        };

        return [addAction];
    }
    // #endregion

    // #region Add/Remove items
    private addEmptyValue(): void {
        this.addToValueList(new PartialRecord({}));
    }

    protected addToValueList(complexValue: PartialRecord): void {
        this.flattenedComplexListFieldRecords.push(complexValue);
        this.list.push(this.createFormGroup());
    }

    // AddressFieldList overrides this to return controls
    protected createFormGroup(): AbstractControl {
        return new FormGroup({});
    }

    private removeItem(index: number): void {
        this.list.removeAt(index);
        this.flattenedComplexListFieldRecords.splice(index, 1);

        // Automatically add new empty value if list is empty
        if (this.list.length === 0) {
            this.addEmptyValue();
        }
    }
    // #endregion
}
